import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getCmsDocument, type CmsPageLocale } from '@/lib/db/cms-pages';
import { findSection } from '@/lib/cms/section-types';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { toast } from 'sonner';

import { Button } from '@/components/Button';
import { LocationCombobox, type LocationOption } from '@/components/LocationCombobox';
import { PhoneField } from '@/components/PhoneField/PhoneField';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';
import { WhatsAppButton } from '@/components/WhatsAppButton';
import {
  SellerPhotoUploader,
  type SellerPhotoUploaderError,
} from '@/components/SellerPhotoUploader/SellerPhotoUploader';
import { env } from '@/lib/env';
import { useRecaptchaToken } from '@/lib/useRecaptchaToken';
import { searchDldBuildings, searchDldCommunities, searchDldZones } from '@/lib/db/dld-locations';
import { postSellerSubmission } from '@/lib/sell-submission-api';
import { SELL_META } from '@/lib/seo/site';
import { useSeoHead } from '@/lib/seo/useSeoHead';
import {
  edgePathToFormField,
  mapFormToPayload,
  REASON_KEYS,
  BEDROOMS_KEYS,
  type ReasonKey,
  type BedroomsKey,
} from '@/lib/sell-submission-schema';

import type { SellFormValues, SellType, SubmitState } from './SellPage.types';

type Step = { number: string; title: string; body: string };

// E.164: '+' followed by a non-zero digit, then 1-14 more digits.
const E164_REGEX = /^\+[1-9]\d{1,14}$/;

function sellFormSchema(t: TFunction) {
  return z.object({
    fullName: z
      .string()
      .trim()
      .min(2, t('sell.form.validation.fullNameRequired'))
      .max(140, t('sell.form.validation.fullNameTooLong')),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, t('sell.form.validation.emailRequired'))
      .email(t('sell.form.validation.emailInvalid'))
      .max(254, t('sell.form.validation.emailInvalid')),
    phone: z
      .string()
      .trim()
      .min(1, t('sell.form.validation.phoneRequired'))
      .regex(E164_REGEX, t('sell.form.validation.phoneInvalidE164')),
    zone: z.string().trim().min(1, t('sell.form.validation.zoneRequired')),
    community: z
      .string()
      .trim()
      .min(1, t('sell.form.validation.communityRequired'))
      .max(100, t('sell.form.validation.communityTooLong')),
    // Required since Patch 6 (ADR-024) — captured at the public submit and
    // used by the accept_seller_submission RPC to auto-compose deals.title.
    building: z
      .string()
      .trim()
      .min(2, t('sell.form.validation.buildingRequired'))
      .max(100, t('sell.form.validation.buildingTooLong')),
    // Required since Patch 6 (ADR-024) — needed for deals.area_sqft NOT NULL
    // on the Draft created at acceptance.
    area: z.string().trim().min(1, t('sell.form.validation.areaRequired')),
    bedrooms: z.string().trim().min(1, t('sell.form.validation.bedroomsRequired')),
    askingPrice: z.string().trim().min(1, t('sell.form.validation.askingRequired')),
    originalPrice: z.string().trim(),
    amountPaid: z.string().trim(),
    reason: z.string().trim(),
    description: z.string().trim().max(1500, t('sell.form.validation.descriptionTooLong')),
  });
}

// Maps a server / mapper raw issue code to an i18n key. The front-side schema
// emits short canonical codes (see sell-submission-schema.ts), and the Edge
// uses similar short tags ('invalid_e164', 'required_off_plan',
// 'amount_exceeds_original'). Anything unmatched falls back to a generic copy.
const ISSUE_TO_I18N: Readonly<Record<string, string>> = {
  required_off_plan: 'sell.form.validation.requiredOffPlan',
  amount_exceeds_original: 'sell.form.validation.amountPaidExceedsOriginal',
  invalid_e164: 'sell.form.validation.phoneInvalidE164',
  invalid_bedrooms: 'sell.form.validation.bedroomsRequired',
  bedrooms_out_of_range: 'sell.form.validation.bedroomsRequired',
  not_a_number: 'sell.form.validation.notANumber',
  not_an_integer: 'sell.form.validation.notAnInteger',
  asking_too_low: 'sell.form.validation.askingTooLow',
  original_too_low: 'sell.form.validation.askingTooLow',
  amount_paid_negative: 'sell.form.validation.notANumber',
  area_out_of_range: 'sell.form.validation.areaOutOfRange',
  name_too_short: 'sell.form.validation.fullNameRequired',
  name_too_long: 'sell.form.validation.fullNameTooLong',
  email_invalid: 'sell.form.validation.emailInvalid',
  zone_required: 'sell.form.validation.zoneRequired',
  zone_too_long: 'sell.form.validation.zoneRequired',
  community_required: 'sell.form.validation.communityRequired',
  community_too_long: 'sell.form.validation.communityTooLong',
  building_required: 'sell.form.validation.buildingRequired',
  building_too_long: 'sell.form.validation.buildingTooLong',
  description_too_long: 'sell.form.validation.descriptionTooLong',
};

function issueMessage(t: TFunction, raw: string): string {
  const key = ISSUE_TO_I18N[raw];
  if (key) return t(key);
  return t('sell.form.validation.genericFieldError');
}

function applyIssuesToForm(
  issues: ReadonlyArray<{ path: string; message: string }>,
  setError: (field: keyof SellFormValues, error: { message: string }) => void,
  t: TFunction,
): boolean {
  let mapped = false;
  for (const issue of issues) {
    const field = edgePathToFormField(issue.path);
    if (!field) continue;
    setError(field, { message: issueMessage(t, issue.message) });
    mapped = true;
  }
  return mapped;
}

export function SellPage() {
  const { t, i18n } = useTranslation();
  useSeoHead(SELL_META);
  const getRecaptchaToken = useRecaptchaToken();

  const [step, setStep] = useState<1 | 2>(1);
  const [type, setType] = useState<SellType>('ready');
  const [consent, setConsent] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [submitState, setSubmitState] = useState<SubmitState>({ kind: 'idle' });

  const cmsLocale = (
    ['en', 'fr', 'ar'].includes(i18n.language?.split('-')[0] ?? '')
      ? (i18n.language.split('-')[0] as CmsPageLocale)
      : 'en'
  ) as CmsPageLocale;
  const [sellTitle, setSellTitle] = useState<string | null>(null);
  const [sellLead, setSellLead] = useState<string | null>(null);
  // CMS copy is strict: loading / absent / hidden / emptied → not rendered,
  // no i18n fallback.
  useEffect(() => {
    getCmsDocument('sell_intro', cmsLocale)
      .then((doc) => {
        const rt = findSection(doc, 'rich_text');
        setSellTitle(rt && rt.visible ? rt.title || null : null);
        setSellLead(rt && rt.visible ? rt.html || null : null);
      })
      .catch(() => {});
  }, [cmsLocale]);
  const [submittedReference, setSubmittedReference] = useState<string>('');
  const [submittedSummary, setSubmittedSummary] = useState<SellFormValues | null>(null);

  const {
    control,
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
    reset,
  } = useForm<SellFormValues>({
    resolver: zodResolver(sellFormSchema(t)),
    defaultValues: {
      fullName: '',
      email: '',
      phone: '',
      zone: '',
      community: '',
      building: '',
      area: '',
      bedrooms: '',
      askingPrice: '',
      originalPrice: '',
      amountPaid: '',
      reason: '',
      description: '',
    },
  });

  // Track zone + community so the building / community comboboxes can
  // narrow their RPC search context (3-level cascading combobox PR-3).
  const zoneValue = useWatch({ control, name: 'zone' });
  const communityValue = useWatch({ control, name: 'community' });

  const searchZoneOptions = useCallback(async (q: string): Promise<LocationOption[]> => {
    const rows = await searchDldZones(q);
    return rows.map((r) => ({
      key: r.zone_key,
      displayName: r.display_name,
      count: r.transaction_count,
    }));
  }, []);

  const searchCommunityOptions = useCallback(
    async (q: string): Promise<LocationOption[]> => {
      const zoneKey = zoneValue ? zoneValue.toLowerCase().trim() : null;
      const rows = await searchDldCommunities(q, zoneKey);
      return rows.map((r) => ({
        key: r.community_key,
        displayName: r.display_name,
        secondary: r.zone ?? undefined,
        count: r.transaction_count,
        zoneKey: r.zone_key ?? undefined,
        zoneDisplayName: r.zone ?? undefined,
      }));
    },
    [zoneValue],
  );

  const searchBuildingOptions = useCallback(
    async (q: string): Promise<LocationOption[]> => {
      const communityKey = communityValue ? communityValue.toLowerCase().trim() : null;
      const zoneKey = zoneValue ? zoneValue.toLowerCase().trim() : null;
      const rows = await searchDldBuildings(q, communityKey, zoneKey);
      return rows.map((r) => ({
        key: r.building_key,
        displayName: r.display_name,
        secondary: r.community_display_name ?? r.community_key ?? undefined,
        count: r.transaction_count,
        communityKey: r.community_key ?? undefined,
        communityDisplayName: r.community_display_name ?? undefined,
        zoneKey: r.zone_key ?? undefined,
        zoneDisplayName: r.zone ?? undefined,
      }));
    },
    [communityValue, zoneValue],
  );

  const isSubmitting = submitState.kind === 'submitting';

  const onSubmit = handleSubmit(async (values) => {
    setSubmitState({ kind: 'submitting' });

    // F1 — block submit when reCAPTCHA site key is absent (dev workstation
    // without env). Spec §M13.6 makes verification mandatory in prod; the
    // env.ts boot guard already crashes in `production`, so this branch is
    // only reachable in `development`.
    if (!env.recaptchaSiteKey) {
      setSubmitState({ kind: 'recaptchaUnavailable' });
      return;
    }

    let token: string | null = null;
    try {
      token = await getRecaptchaToken('seller');
    } catch {
      token = null;
    }
    if (!token) {
      setSubmitState({ kind: 'recaptchaUnavailable' });
      return;
    }

    // Capture i18n.language at submit; normalise variants (`fr-FR`, `en-US`)
    // back to the canonical 2-char locale code. Patch 6 (ADR-024).
    const localeBase = (i18n.language ?? 'en').split('-')[0]?.toLowerCase();
    const preferredLanguage: 'en' | 'fr' | 'ar' =
      localeBase === 'fr' || localeBase === 'ar' ? localeBase : 'en';
    const mapped = mapFormToPayload(values, type, preferredLanguage);
    if (!mapped.ok) {
      const issues = mapped.issues.map((i) => ({
        path: String(i.path[0] ?? ''),
        message: i.message,
      }));
      const wasMapped = applyIssuesToForm(issues, setError, t);
      if (!wasMapped) toast.error(t('sell.form.errors.genericError'));
      setSubmitState({ kind: 'idle' });
      return;
    }

    const result = await postSellerSubmission({
      payload: mapped.payload,
      recaptchaToken: token,
      photos,
    });

    if (result.ok) {
      setSubmittedReference(result.referenceId);
      setSubmittedSummary(values);
      setStep(2);
      if (typeof window !== 'undefined') {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }
      setSubmitState({ kind: 'idle' });
      return;
    }

    switch (result.code) {
      case 'rate_limited':
        setSubmitState({ kind: 'rateLimited' });
        return;
      case 'recaptcha_failed':
        // Spec §M8 L1236 — blocage silencieux. Pas de toast utilisateur.
        // En dev seulement, on émet un warn pour aider le diagnostic.
        if (env.isDev) {
          console.warn('[sell] recaptcha verification failed');
        }
        setSubmitState({ kind: 'idle' });
        return;
      case 'invalid_payload': {
        const issues = result.issues ?? [];
        const wasMapped = applyIssuesToForm(issues, setError, t);
        if (!wasMapped) toast.error(t('sell.form.errors.genericError'));
        setSubmitState({ kind: 'idle' });
        return;
      }
      case 'invalid_photo':
        toast.error(t('sell.form.errors.genericError'));
        setSubmitState({ kind: 'idle' });
        return;
      case 'recaptcha_unreachable':
      case 'internal_error':
      case 'method_not_allowed':
      case 'network':
      default:
        setSubmitState({ kind: 'genericError' });
        return;
    }
  });

  const handlePhotoError = (kind: SellerPhotoUploaderError) => {
    if (kind === 'too_large') toast.error(t('sell.form.errors.photoTooLarge'));
    else if (kind === 'invalid_type') toast.error(t('sell.form.errors.photoInvalidType'));
    else toast.error(t('sell.form.errors.photoCapReached'));
  };

  const restart = () => {
    reset();
    setSubmittedReference('');
    setSubmittedSummary(null);
    setConsent(false);
    setType('ready');
    setPhotos([]);
    setSubmitState({ kind: 'idle' });
    setStep(1);
  };

  const steps = t('sell.steps', { returnObjects: true }) as Step[];

  const submitDisabled = !consent || isSubmitting;

  return (
    <div className="public">
      <section className="sell-hero">
        <div className="sell-hero__inner">
          <div className="sell-hero__kicker">{t('sell.kicker')}</div>
          {sellTitle && <h1 className="sell-hero__title">{sellTitle}</h1>}
          {sellLead && (
            <div className="sell-hero__lead" dangerouslySetInnerHTML={{ __html: sellLead }} />
          )}
          <div className="sell-hero__steps">
            {steps.map((s) => (
              <div key={s.number} className="sell-hero__step">
                <div className="sell-hero__step-number">{s.number}</div>
                <div className="sell-hero__step-title">{s.title}</div>
                <div className="sell-hero__step-body">{s.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="sell-form-shell">
        {step === 2 && submittedSummary ? (
          <div className="sell-success">
            <div className="sell-success__icon" aria-hidden="true">
              ◆
            </div>
            <h2 className="sell-success__title">{t('sell.success.title')}</h2>
            <p className="sell-success__lead">{t('sell.success.lead')}</p>

            {submittedReference ? (
              <div className="sell-success__reference" data-testid="sell-success-reference">
                <span className="sell-success__reference-label">
                  {t('sell.success.referenceLabel')}
                </span>
                <span className="sell-success__reference-value">{submittedReference}</span>
              </div>
            ) : null}

            <div className="sell-success__summary">
              <div className="sell-success__summary-title">{t('sell.success.summaryTitle')}</div>
              {(
                [
                  ['zone', submittedSummary.zone],
                  ['community', submittedSummary.community],
                  ['type', t(`sell.form.${type}`)],
                  ['askingPrice', submittedSummary.askingPrice],
                ] as const
              ).map(([key, value]) => (
                <div key={key} className="sell-success__summary-row">
                  <span className="sell-success__summary-label">{t(`sell.form.${key}`)}</span>
                  <span className="sell-success__summary-value">{value}</span>
                </div>
              ))}
            </div>
            <button type="button" className="sell-success__again" onClick={restart}>
              {t('sell.success.again')}
            </button>
          </div>
        ) : (
          <form className="sell-form" onSubmit={onSubmit} noValidate>
            <div className="sell-form__kicker">{t('sell.form.kicker')}</div>

            {submitState.kind === 'rateLimited' ? (
              <div
                className="sell-form__banner sell-form__banner--rate-limit"
                role="alert"
                data-testid="sell-rate-limit-banner"
              >
                <p className="sell-form__banner-message">
                  {t('sell.form.errors.rateLimitedBanner')}
                </p>
                <WhatsAppButton
                  label={t('sell.form.errors.rateLimitedCta')}
                  size="md"
                  className="sell-form__banner-cta"
                />
              </div>
            ) : null}

            {submitState.kind === 'recaptchaUnavailable' ? (
              <div
                className="sell-form__banner sell-form__banner--info"
                role="alert"
                data-testid="sell-recaptcha-unavailable-banner"
              >
                <p className="sell-form__banner-message">
                  {t('sell.form.errors.recaptchaUnavailable')}
                </p>
                <WhatsAppButton
                  label={t('sell.form.errors.rateLimitedCta')}
                  size="md"
                  className="sell-form__banner-cta"
                />
              </div>
            ) : null}

            {submitState.kind === 'genericError' ? (
              <div
                className="sell-form__banner sell-form__banner--error"
                role="alert"
                data-testid="sell-generic-error-banner"
              >
                <p className="sell-form__banner-message">{t('sell.form.errors.genericError')}</p>
              </div>
            ) : null}

            <div className="sell-form__type">
              <div className="sell-form__type-label">{t('sell.form.type')} *</div>
              <div className="sell-form__type-row" role="group">
                {(['ready', 'offPlan'] as SellType[]).map((tt) => (
                  <button
                    key={tt}
                    type="button"
                    aria-pressed={type === tt}
                    className={`sell-form__chip${type === tt ? ' is-active' : ''}`}
                    onClick={() => setType(tt)}
                  >
                    {t(`sell.form.${tt}`)}
                  </button>
                ))}
              </div>
            </div>

            <div className="sell-form__row">
              <TextField
                label={t('sell.form.fullName')}
                placeholder={t('sell.form.fullNamePlaceholder')}
                autoComplete="name"
                required
                error={errors.fullName?.message}
                {...register('fullName')}
              />
              <TextField
                label={t('sell.form.email')}
                type="email"
                placeholder={t('sell.form.emailPlaceholder')}
                autoComplete="email"
                inputMode="email"
                required
                error={errors.email?.message}
                {...register('email')}
              />
              <Controller
                control={control}
                name="phone"
                render={({ field, fieldState }) => (
                  <PhoneField
                    name="phone"
                    label={t('sell.form.phone')}
                    placeholder={t('sell.form.phonePlaceholder')}
                    required
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    error={fieldState.error?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name="zone"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="zone"
                    label={t('sell.form.zone')}
                    placeholder={t('sell.form.zoneSelect')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onSearch={searchZoneOptions}
                  />
                )}
              />
              <Controller
                control={control}
                name="community"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="community"
                    label={t('sell.form.community')}
                    placeholder={t('sell.form.communityPlaceholder')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onSearch={searchCommunityOptions}
                    onCommitOption={(opt) => {
                      // Auto-fill the parent zone when the seller picks a
                      // community whose canonical zone is known.
                      if (opt.zoneDisplayName) {
                        setValue('zone', opt.zoneDisplayName, { shouldDirty: true });
                      }
                    }}
                    /* allowFreeText defaults to true — the seller may live in
                     * a freshly delivered community that has no DLD entry
                     * yet. The admin reconciles canonical naming at
                     * acceptance time. A future PR may switch this to strict
                     * once the DLD referential is fed by the official Dubai
                     * Pulse API (Phase 2). */
                  />
                )}
              />
              <Controller
                control={control}
                name="building"
                render={({ field, fieldState }) => (
                  <LocationCombobox
                    name="building"
                    label={t('sell.form.building')}
                    placeholder={t('sell.form.buildingPlaceholder')}
                    required
                    error={fieldState.error?.message}
                    value={field.value ?? ''}
                    onChange={field.onChange}
                    onSearch={searchBuildingOptions}
                    onCommitOption={(opt) => {
                      if (opt.communityDisplayName) {
                        setValue('community', opt.communityDisplayName, { shouldDirty: true });
                      }
                      if (opt.zoneDisplayName) {
                        setValue('zone', opt.zoneDisplayName, { shouldDirty: true });
                      }
                    }}
                    /* Building free-text allowed for sellers — a freshly
                     * delivered tower may not yet appear in the DLD
                     * referential, and the seller is the most likely person
                     * to know its real name. The admin reconciles at
                     * acceptance time. */
                    allowFreeText
                  />
                )}
              />
              <TextField
                label={t('sell.form.area')}
                placeholder={t('sell.form.areaPlaceholder')}
                inputMode="numeric"
                required
                error={errors.area?.message}
                {...register('area')}
              />
              <div className="m5-field">
                <label className="m5-field__label" htmlFor="sell-bedrooms">
                  {t('sell.form.bedrooms')}
                  <span className="m5-field__required" aria-hidden="true">
                    *
                  </span>
                </label>
                <div className="m5-field__control">
                  <select
                    id="sell-bedrooms"
                    className="m5-field__input"
                    defaultValue=""
                    aria-invalid={errors.bedrooms ? true : undefined}
                    {...register('bedrooms')}
                  >
                    <option value="" disabled>
                      —
                    </option>
                    {BEDROOMS_KEYS.map((key: BedroomsKey) => (
                      <option key={key} value={key}>
                        {t(`sell.form.bedroomsOptions.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.bedrooms?.message ? (
                  <span className="m5-field__error" role="alert">
                    {errors.bedrooms.message}
                  </span>
                ) : null}
              </div>
              <TextField
                label={t('sell.form.askingPrice')}
                placeholder={t('sell.form.askingPricePlaceholder')}
                inputMode="numeric"
                required
                error={errors.askingPrice?.message}
                {...register('askingPrice')}
              />
            </div>

            <div className={`sell-form__offplan${type === 'offPlan' ? ' is-open' : ''}`}>
              <div className="sell-form__offplan-inner">
                <div className="sell-form__row">
                  <TextField
                    label={t('sell.form.originalPrice')}
                    placeholder={t('sell.form.originalPricePlaceholder')}
                    inputMode="numeric"
                    required={type === 'offPlan'}
                    error={errors.originalPrice?.message}
                    {...register('originalPrice')}
                  />
                  <TextField
                    label={t('sell.form.amountPaid')}
                    placeholder={t('sell.form.amountPaidPlaceholder')}
                    inputMode="numeric"
                    required={type === 'offPlan'}
                    error={errors.amountPaid?.message}
                    {...register('amountPaid')}
                  />
                </div>
              </div>
            </div>

            <div className="m5-field" style={{ marginTop: 16 }}>
              <label className="m5-field__label" htmlFor="sell-reason">
                {t('sell.form.reason')}
              </label>
              <div className="m5-field__control">
                <select
                  id="sell-reason"
                  className="m5-field__input"
                  defaultValue=""
                  {...register('reason')}
                >
                  <option value="">—</option>
                  {REASON_KEYS.map((key: ReasonKey) => (
                    <option key={key} value={key}>
                      {t(`sell.form.reasons.${key}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <Textarea
              label={t('sell.form.description')}
              placeholder={t('sell.form.descriptionPlaceholder')}
              rows={3}
              error={errors.description?.message}
              {...register('description')}
            />

            <div style={{ marginTop: 24 }}>
              <div className="sell-form__type-label">{t('sell.form.photos.label')}</div>
              <SellerPhotoUploader
                value={photos}
                onChange={setPhotos}
                onError={handlePhotoError}
                t={t}
              />
            </div>

            <label className="sell-form__consent" htmlFor="sell-consent">
              <input
                id="sell-consent"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
              />
              <span className="sell-form__consent-label">{t('sell.form.consent')}</span>
            </label>

            <div className="sell-form__footer">
              <span className="sell-form__notice">{t('sell.form.recaptchaNotice')}</span>
              {!consent && !isSubmitting && (
                <span
                  style={{
                    fontSize: '0.75rem',
                    color: 'var(--c-danger, #dc2626)',
                  }}
                >
                  {t('sell.form.consentRequired')}
                </span>
              )}
              <Button
                type="submit"
                variant="primary"
                disabled={submitDisabled}
                loading={isSubmitting}
                loadingLabel="…"
                style={{ width: 'auto', minWidth: 160 }}
              >
                {t('sell.form.submit')}
              </Button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
